/**
 * Read an .xlsb workbook.
 *
 * ExcelJS cannot: .xlsb is BIFF12, a binary record stream, not the XML that
 * .xlsx is. The client's own file is .xlsb, so "convert it to .xlsx first" is a
 * step somebody has to remember every time, and the step they will get wrong.
 *
 * This is a port of the parser used to extract that exact file during the data
 * import, so the format work is proven rather than theoretical. It reads only
 * what a mould list needs - shared strings, cell values, and the drawing anchors
 * that put a photo on a row. Formulas are read as their cached result, which is
 * what a spreadsheet of specifications contains anyway.
 *
 * ## The format, briefly
 *
 * An .xlsb is a zip, like .xlsx. Inside, each part is a stream of records:
 *
 *     [record id: 1-2 bytes varint] [length: 1-4 bytes varint] [payload]
 *
 * The id is one byte when under 128; otherwise the low byte has its top bit set
 * and a second byte carries the rest. The length is up to four bytes, seven
 * bits each, little-endian. Everything else is reading fixed offsets out of the
 * payload.
 */
import { unzipSync } from 'fflate';
// --- record ids we care about ---
const BrtRowHdr = 0;
const BrtCellBlank = 1;
const BrtCellRk = 2;
const BrtCellError = 3;
const BrtCellBool = 4;
const BrtCellReal = 5;
const BrtCellSt = 6;
const BrtCellIsst = 7;
const BrtFmlaString = 8;
const BrtFmlaNum = 9;
const BrtFmlaBool = 10;
const BrtSSTItem = 19;
const VALUE_RECORDS = new Set([
    BrtCellBlank,
    BrtCellRk,
    BrtCellError,
    BrtCellBool,
    BrtCellReal,
    BrtCellSt,
    BrtCellIsst,
    BrtFmlaString,
    BrtFmlaNum,
    BrtFmlaBool,
]);
/** Walk the record stream. Yields [id, payload] until the buffer runs out. */
export function* records(buf) {
    let i = 0;
    while (i < buf.length) {
        // --- record id ---
        let id = buf[i];
        i += 1;
        if (id & 0x80) {
            if (i >= buf.length)
                return;
            id = (id & 0x7f) | ((buf[i] & 0x7f) << 7);
            i += 1;
        }
        // --- length: up to four 7-bit groups ---
        let length = 0;
        for (let shift = 0; shift < 4; shift++) {
            if (i >= buf.length)
                return;
            const byte = buf[i];
            i += 1;
            length |= (byte & 0x7f) << (7 * shift);
            if (!(byte & 0x80))
                break;
        }
        if (i + length > buf.length)
            return; // truncated tail - stop rather than throw
        yield [id, buf.subarray(i, i + length)];
        i += length;
    }
}
/** XLWideString: a 4-byte character count, then UTF-16LE. */
export function wideString(view, buf, offset) {
    const count = view.getUint32(offset, true);
    const start = offset + 4;
    const end = Math.min(start + count * 2, buf.length);
    let out = '';
    for (let p = start; p + 1 < end; p += 2) {
        out += String.fromCharCode(buf[p] | (buf[p + 1] << 8));
    }
    return out;
}
/**
 * RkNumber: a float squeezed into 32 bits.
 *
 * Bit 0 says "divide by 100", bit 1 says "this is an integer". Otherwise the
 * remaining 30 bits are the *high* half of an IEEE double with the low half
 * zeroed - which is why the mantissa is shifted up rather than read directly.
 */
function rkValue(rk) {
    const isX100 = (rk & 1) !== 0;
    const isInt = (rk & 2) !== 0;
    let value;
    if (isInt) {
        const raw = rk >> 2;
        value = raw >= 0x20000000 ? raw - 0x40000000 : raw;
    }
    else {
        const bytes = new ArrayBuffer(8);
        const dv = new DataView(bytes);
        dv.setUint32(4, rk & 0xfffffffc, true); // high word, low word left at zero
        value = dv.getFloat64(0, true);
    }
    return isX100 ? value / 100 : value;
}
export function sharedStrings(part) {
    if (!part)
        return [];
    const out = [];
    const view = new DataView(part.buffer, part.byteOffset, part.byteLength);
    for (const [id, payload] of records(part)) {
        if (id !== BrtSSTItem)
            continue;
        const p = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
        void view;
        // One flag byte, then the string.
        out.push(wideString(p, payload, 1));
    }
    return out;
}
export function sheetCells(part, sst) {
    const cells = new Map();
    let row = -1;
    let maxRow = 0;
    let maxCol = 0;
    for (const [id, payload] of records(part)) {
        const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
        if (id === BrtRowHdr) {
            if (payload.length >= 4)
                row = view.getUint32(0, true);
            continue;
        }
        if (!VALUE_RECORDS.has(id) || payload.length < 8)
            continue;
        const col = view.getUint32(0, true);
        let value = null;
        if (id === BrtCellIsst && payload.length >= 12) {
            const index = view.getUint32(8, true);
            value = index < sst.length ? sst[index] : '';
        }
        else if ((id === BrtCellSt || id === BrtFmlaString) && payload.length >= 12) {
            value = wideString(view, payload, 8);
        }
        else if (id === BrtCellRk && payload.length >= 12) {
            value = rkValue(view.getUint32(8, true));
        }
        else if ((id === BrtCellReal || id === BrtFmlaNum) && payload.length >= 16) {
            value = view.getFloat64(8, true);
        }
        else if ((id === BrtCellBool || id === BrtFmlaBool) && payload.length >= 9) {
            value = payload[8] !== 0;
        }
        if (value === null || value === '')
            continue;
        cells.set(`${row},${col}`, value);
        if (row > maxRow)
            maxRow = row;
        if (col > maxCol)
            maxCol = col;
    }
    return { cells, maxRow, maxCol };
}
/**
 * Bytes to base64, in a browser or in Node.
 *
 * `btoa` does not exist in Node and `Buffer` does not exist in a browser, and
 * this parser now has one consumer in each. Chunked because
 * `String.fromCharCode(...bytes)` on a 200KB photo blows the argument limit.
 */
function toBase64(bytes) {
    const maybeBuffer = globalThis
        .Buffer;
    if (maybeBuffer)
        return maybeBuffer.from(bytes).toString('base64');
    let binary = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
}
/** `<xdr:from><xdr:row>` → the media file anchored there. */
function imagesByRow(drawing, rels, files) {
    const out = new Map();
    if (!drawing || !rels)
        return out;
    // rId → media path.
    const target = new Map();
    for (const m of rels.matchAll(/Id="([^"]+)"[^>]*Target="([^"]+)"/g)) {
        target.set(m[1], m[2].replace(/^\.\.\//, 'xl/'));
    }
    // Each anchor block carries its top row and the rId of its picture.
    for (const block of drawing.split(/<xdr:(?:one|two)CellAnchor|<xdr:absoluteAnchor/).slice(1)) {
        const rowMatch = /<xdr:from>[\s\S]*?<xdr:row>(\d+)<\/xdr:row>/.exec(block);
        const embed = /r:embed="([^"]+)"/.exec(block);
        if (!rowMatch || !embed)
            continue;
        const path = target.get(embed[1]);
        if (!path)
            continue;
        const bytes = files[path];
        if (!bytes)
            continue;
        const row = Number(rowMatch[1]) + 1; // 0-based in the XML, 1-based to callers
        if (out.has(row))
            continue;
        const ext = (path.split('.').pop() ?? 'png').toLowerCase();
        out.set(row, `data:image/${ext === 'jpg' ? 'jpeg' : ext};base64,${toBase64(bytes)}`);
    }
    return out;
}
/**
 * Parse an .xlsb into the first sheet's cells and images.
 *
 * Only the first sheet: the mould list is one sheet, and guessing which of
 * several is "the data" is a worse failure than saying so.
 */
export function parseXlsb(data) {
    const files = unzipSync(data);
    const sheetPath = Object.keys(files).find((f) => /^xl\/worksheets\/sheet1\.bin$/i.test(f));
    if (!sheetPath) {
        throw new Error('That .xlsb has no readable worksheet. Was it saved by Excel?');
    }
    const sst = sharedStrings(files['xl/sharedStrings.bin']);
    const { cells, maxRow, maxCol } = sheetCells(files[sheetPath], sst);
    const decoder = new TextDecoder();
    const drawingPath = Object.keys(files).find((f) => /^xl\/drawings\/drawing1\.xml$/i.test(f));
    const relsPath = Object.keys(files).find((f) => /^xl\/drawings\/_rels\/drawing1\.xml\.rels$/i.test(f));
    const images = imagesByRow(drawingPath ? decoder.decode(files[drawingPath]) : undefined, relsPath ? decoder.decode(files[relsPath]) : undefined, files);
    // The sheet's display name lives in workbook.bin as a record; the XML rels
    // do not carry it. Not worth another record walk for a label.
    const name = 'Sheet 1';
    return {
        name,
        rowCount: maxRow + 1,
        columnCount: maxCol + 1,
        images,
        cell: (row, col) => cells.get(`${row - 1},${col - 1}`) ?? null,
    };
}
//# sourceMappingURL=xlsb.js.map